import { BarChart, BarChartProps } from '@mui/x-charts/BarChart';
import { formatMoney } from '@dill/dill-shared';
import { COLORS } from '../../../../utils/colors';
import { InvoiceVolumePoint } from './chartTypes';

interface InvoiceVolumeChartProps {
  data: InvoiceVolumePoint[];
  // Label bars "JAN '25" instead of "JAN" - needed when the data spans more than one year,
  // otherwise months from different years share a label.
  showYear?: boolean;
}

const getResponsiveConfig = (): Partial<BarChartProps> => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return {
    height: isMobile ? 280 : 320,
    margin: {
      left: 0,
      right: isMobile ? 10 : 16,
      top: 16,
      bottom: isMobile ? 60 : 48,
    },
    hideLegend: true,
  };
};

function InvoiceVolumeChart({ data, showYear = false }: InvoiceVolumeChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
        No invoice volume data available
      </div>
    );
  }

  const dataSet = data.map((point) => {
    const [year, month] = point.month.split('-');
    return {
      month: showYear ? `${month} '${year.slice(-2)}` : month,
      totalInvoiced: point.totalInvoiced,
    };
  });

  return (
    <BarChart
      borderRadius={8}
      dataset={dataSet}
      xAxis={[
        {
          dataKey: 'month',
          tickLabelStyle: {
            fontSize: 11,
            fill: COLORS.TEXT_SECONDARY,
            fontFamily: 'Inter',
          },
          disableLine: true,
          disableTicks: true,
        },
      ]}
      series={[
        {
          dataKey: 'totalInvoiced',
          label: 'Invoiced',
          color: COLORS.PRIMARY_500,
          valueFormatter: (value: number | null) => {
            return `${formatMoney(value || 0, 2)}`;
          },
        },
      ]}
      yAxis={[
        {
          width: 56,
          tickLabelStyle: {
            fontSize: 10,
            fill: COLORS.TEXT_SECONDARY,
            fontFamily: 'Inter',
          },
          disableLine: true,
          disableTicks: true,
          domainLimit: (min, max) => {
            return { min, max: Math.floor(max + (max * 10) / 100) };
          },
          valueFormatter: (value: number) => {
            return formatMoney(value, 0);
          },
        },
      ]}
      grid={{ horizontal: true }}
      sx={{
        '& .MuiChartsGrid-line': {
          stroke: COLORS.GREY_100,
          strokeDasharray: '3 3',
        },
      }}
      {...getResponsiveConfig()}
    />
  );
}

export default InvoiceVolumeChart;
